import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { previewDesignStage, useContractorPreview } from "@/features/orders/contractorPreview";
import { AlertTriangle, ExternalLink, Info } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import {
  useCostEstimate,
  useCostEstimateByOrder,
  useCostEstimateMutations,
} from "../hooks";
import {
  COST_ESTIMATE_EXECUTOR_STATES,
  COST_ESTIMATE_STATE_COLOR,
  COST_ESTIMATE_STATE_LABEL,
  type CostEstimate,
  type CostEstimateState,
  type PowerProject,
} from "../types";
import { WorkUnitPanel } from "./WorkUnitPanel";
import { DesignOrderForm, OrderLinksPreview } from "./DesignOrderForm";
import { StageProceedingPanel } from "./StageProceedingPanel";
import { StageHistory } from "./TechnicalProjectTab";
import { Button } from "@/components/ui/Button";
import { fmtDateTime } from "../utils";

const stateOptions = COST_ESTIMATE_EXECUTOR_STATES.map((v) => ({
  value: v,
  label: COST_ESTIMATE_STATE_LABEL[v],
}));

const ghostBtn =
  "inline-flex items-center gap-1 px-2.5 py-1.5 rounded text-xs font-medium border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg-muted)]";
const primaryBtn =
  "inline-flex items-center gap-1 px-2.5 py-1.5 rounded bg-[var(--color-primary)] text-white text-xs font-medium hover:opacity-90 disabled:opacity-50";

/** Dane zwycięzcy dla formularza zlecenia — tylko gdy postępowanie czeka na zlecenie. */
function winnerProps(stage: CostEstimate) {
  const p = stage.proceeding;
  if (!p?.awaiting_order || !p.winner) return undefined;
  return {
    proceedingId: p.id,
    number: p.number,
    title: p.title,
    contractorName: p.winner.contractor_name,
    amountNet: p.winner.amount_net,
  };
}

/** Kosztorys zlecony „po staremu" jako pozycja zamówienia PT — osobne zlecenie etapu
 *  zapłaciłoby za tę samą pracę drugi raz. Ostrzeżenie, nie blokada. */
function PtCostItemsWarning({ ce }: { ce: CostEstimate }) {
  if (!ce.can_manage || ce.pt_orders_with_cost_items.length === 0) return null;
  return (
    <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-300">
      <AlertTriangle size={13} className="mt-0.5 shrink-0" />
      <div className="space-y-1">
        <p>
          Kosztorys jest już ujęty w zamówieniu projektu technicznego. Osobne zlecenie etapu
          „Kosztorys” oznacza podwójne zamówienie tej samej pracy.
        </p>
        <ul className="space-y-0.5">
          {ce.pt_orders_with_cost_items.map((o) => (
            <li key={o.order_id}>
              <Link
                to={`/orders/${o.order_id}`}
                className="inline-flex items-center gap-1 font-semibold hover:underline"
              >
                {o.order_number} <ExternalLink size={11} />
              </Link>
              {o.contractor_name ? ` (${o.contractor_name})` : ""}: {o.items.join("; ")}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function AssignmentBlock({ ce, connId }: { ce: CostEstimate; connId: string }) {
  const m = useCostEstimateMutations(connId);
  const { data: users = [] } = useInternalUsers();
  const [employee, setEmployee] = useState("");
  const [showOrder, setShowOrder] = useState(false);
  const userOptions = useMemo(
    () =>
      [...users]
        .sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"))
        .map((u) => ({ value: u.id, label: u.full_name })),
    [users],
  );

  if (ce.assignee) {
    return (
      <div className="space-y-1.5">
        {ce.assignee.kind === "employee" ? (
          <p className="text-xs text-[var(--color-text-muted)]">
            Realizuje pracownik GK:{" "}
            <span className="font-semibold text-[var(--color-text)]">
              {ce.assignee.name}
            </span>
          </p>
        ) : (
          <p className="text-xs text-[var(--color-text-muted)]">
            Realizuje podwykonawca:{" "}
            <span className="font-semibold text-[var(--color-text)]">
              {ce.assignee.name ?? "—"}
            </span>
          </p>
        )}
        <OrderLinksPreview links={ce.order_links} />
        <StageProceedingPanel
          proceeding={ce.proceeding}
          canManage={ce.can_manage}
          stage="cost_estimate"
          starting={m.startRfqProceeding.isPending}
          releasing={m.releaseRfqProceeding.isPending}
          onStart={(payload) => m.startRfqProceeding.mutate({ id: ce.id, payload })}
          onRelease={() => m.releaseRfqProceeding.mutate(ce.id)}
          onOrderWinner={() => setShowOrder(true)}
          orderFormOpen={showOrder}
        />
        {ce.can_manage && !showOrder && !ce.proceeding?.blocks_direct_order && (
          <button
            type="button"
            onClick={() => setShowOrder(true)}
            className={ghostBtn}
          >
            <AlertTriangle size={13} /> Zamówienie korekcyjne (poprawki)
          </button>
        )}
        {ce.can_manage && showOrder && (
          <DesignOrderForm
            kind="correction"
            winner={winnerProps(ce)}
            suggestScope="cost_estimate"
            connectionId={connId}
            pending={m.createRequisition.isPending}
            onSubmit={(fd) =>
              m.createRequisition.mutate(
                { id: ce.id, fd },
                { onSuccess: () => setShowOrder(false) },
              )
            }
            onCancel={() => setShowOrder(false)}
          />
        )}
      </div>
    );
  }
  if (!ce.can_manage) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Wykonawca nie został jeszcze przypisany.
      </p>
    );
  }
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="w-72">
          <SearchableSelect
            options={userOptions}
            value={employee}
            placeholder="— pracownik GK —"
            onChange={(e) => setEmployee(e.target.value)}
          />
        </div>
        <button
          type="button"
          disabled={!employee || m.assignEmployee.isPending}
          onClick={() => m.assignEmployee.mutate({ id: ce.id, employee })}
          className={primaryBtn}
        >
          Przypisz pracownika
        </button>
        {!showOrder && !ce.proceeding?.blocks_direct_order && (
          <button
            type="button"
            onClick={() => setShowOrder(true)}
            className={ghostBtn}
          >
            …albo zleć podwykonawcy
          </button>
        )}
      </div>
      <StageProceedingPanel
        proceeding={ce.proceeding}
        canManage={ce.can_manage}
        stage="cost_estimate"
        starting={m.startRfqProceeding.isPending}
        releasing={m.releaseRfqProceeding.isPending}
        onStart={(payload) => m.startRfqProceeding.mutate({ id: ce.id, payload })}
        onRelease={() => m.releaseRfqProceeding.mutate(ce.id)}
        onOrderWinner={() => setShowOrder(true)}
        orderFormOpen={showOrder}
      />
      {showOrder && (
        <DesignOrderForm
          winner={winnerProps(ce)}
          suggestScope="cost_estimate"
          connectionId={connId}
          showTenderDocs
          showRouteDxf
          pending={m.createRequisition.isPending}
          onSubmit={(fd) =>
            m.createRequisition.mutate(
              { id: ce.id, fd },
              { onSuccess: () => setShowOrder(false) },
            )
          }
          onCancel={() => setShowOrder(false)}
        />
      )}
    </div>
  );
}

export function KosztorysTab({
  connection,
  orderId,
}: {
  connection?: PowerProject;
  orderId?: string;
}) {
  const byConn = useCostEstimate(orderId ? undefined : connection?.id);
  const byOrder = useCostEstimateByOrder(orderId);
  // „Widok podwykonawcy": flagi z API są dla użytkownika GK — przeliczamy je jak dla wykonawcy.
  const { isPreview } = useContractorPreview();
  const ce = orderId
    ? byOrder.data && isPreview
      ? previewDesignStage(byOrder.data, orderId)
      : byOrder.data
    : byConn.data;
  const isLoading = orderId ? byOrder.isLoading : byConn.isLoading;
  const connId = ce?.connection ?? connection?.id ?? "";
  const m = useCostEstimateMutations(connId);

  // Embedded on the subcontractor's Order page: self-hide when there's no kosztorys for it.
  if (orderId && (isLoading || !ce)) return null;
  if (isLoading)
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  if (!ce)
    return (
      <p className="text-sm text-[var(--color-text-muted)] py-6">
        Brak dostępu do kosztorysu.
      </p>
    );

  // Kosztorys runs independently of the PT (it may go to another firm in parallel), so an
  // unaccepted PT only warns — on acceptance below — never locks the stage.
  const acceptCostEstimate = () => {
    if (
      !ce.pt_accepted &&
      !window.confirm(
        "Projekt techniczny (PT) nie jest jeszcze zaakceptowany przez GK.\n\n" +
          "Kosztorys może się zdezaktualizować, jeśli PT zostanie jeszcze zmieniony. " +
          "Zaakceptować kosztorys mimo to?",
      )
    )
      return;
    m.acceptGk.mutate({ id: ce.id });
  };

  const busy =
    m.setState.isPending ||
    m.acceptGk.isPending ||
    m.requestCorrection.isPending ||
    m.addFiles.isPending ||
    m.addComment.isPending;

  return (
    <div className="space-y-4 pt-2 pb-8">
      {orderId && (
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-[var(--color-text)]">
            Kosztorys — ścieżka operacyjna
          </h2>
          <span className="text-[10px] font-bold px-1.5 py-px rounded bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
            DLC_01
          </span>
        </div>
      )}
      {!orderId && (
        <section className="card p-4 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="section-label">Wykonawca</h3>
            <span className="text-[10px] font-bold px-1.5 py-px rounded bg-gradient-to-r from-indigo-500 to-fuchsia-500 text-white">
              DLC_01
            </span>
          </div>
          <PtCostItemsWarning ce={ce} />
          <AssignmentBlock ce={ce} connId={connId} />
        </section>
      )}
      {!orderId && !ce.pt_accepted && (
        <div className="card p-3 flex items-start gap-2 text-xs text-[var(--color-text-muted)]">
          <Info size={14} className="mt-0.5 shrink-0" />
          <span>
            Projekt techniczny (PT) nie jest jeszcze zaakceptowany przez GK. Kosztorys można
            zlecić i prowadzić równolegle — po zmianach w PT sprawdź, czy jest aktualny.
          </span>
        </div>
      )}
      <WorkUnitPanel
        title="Dokumentacja kosztorysowa i powiązana"
        files={ce.attachments}
        timeline={ce.timeline}
        statusLabels={COST_ESTIMATE_STATE_LABEL}
        canWrite={ce.can_write}
        canManage={ce.can_manage}
        busy={busy}
        state={ce.state}
        stateLabel={
          COST_ESTIMATE_STATE_LABEL[ce.state as CostEstimateState] ??
          ce.state_display
        }
        stateColor={
          COST_ESTIMATE_STATE_COLOR[ce.state as CostEstimateState] ?? "#6b7280"
        }
        stateOptions={stateOptions}
        onSetState={(state) => m.setState.mutate({ id: ce.id, state })}
        onAccept={acceptCostEstimate}
        acceptLabel="Weryfikacja pozytywna"
        acceptDisabled={ce.state === "accepted"}
        onRequestCorrection={(note) =>
          m.requestCorrection.mutate({ id: ce.id, note })
        }
        onAddFiles={(files) => m.addFiles.mutate({ id: ce.id, files })}
        onComment={(body) => m.addComment.mutate({ id: ce.id, body })}
      />

      {/* Rozliczenie kosztorysu — dostępne po akceptacji GK (jak w Projekcie technicznym). */}
      {ce.can_manage && ce.state === "accepted" && !ce.settled_at && (
        <section className="card p-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="section-label">Rozliczenie kosztorysu</div>
            <p className="text-xs text-[var(--color-text-muted)]">
              Oznacz zaakceptowany kosztorys jako rozliczony finansowo.
            </p>
          </div>
          <Button
            size="sm"
            disabled={m.settle.isPending}
            onClick={() => m.settle.mutate({ id: ce.id })}
          >
            Rozlicz kosztorys
          </Button>
        </section>
      )}
      {ce.settled_at && (
        <div className="card p-3 text-xs text-[var(--color-text-muted)]">
          Rozliczono {fmtDateTime(ce.settled_at)}
          {ce.settlement_note ? ` — ${ce.settlement_note}` : ""}
        </div>
      )}

      {!orderId && (
        <StageHistory connId={connId} kinds="cost_estimate_changed" />
      )}
    </div>
  );
}
